'use client';

import { useTranslations } from 'next-intl';
import { useCallback } from 'react';
import { toast } from '@/app/components/ui/use-toast';
import { TRACE_EVENT_ID, TRACE_EVENT_NAME } from '@/app/constants';
import { useFetch } from '@/app/hooks';
import type { ActivityInteractionParams } from '@/app/types';
import { CommunityEnum } from '@/app/types';
import { reportEvent } from '@/app/utils';
import { usePathname } from '@/i18n/routing';
import { activitiesCancelLikeWork, activitiesLikeWork } from '../apis';
import { useCommunityInteraction } from './use-community-interaction';

export interface UseActivityInteractionConfig {
  onInteractionStatusChange?: (params: { id: string; voted: boolean }) => void;
  /** 自定义埋点位置 */
  interactionPosition?: string;
}

/**
 * 活动作品交互 Hook
 * 支持活动点赞和灵感点赞两种模式
 * - isInspiration 为 true 时，复用 useCommunityInteraction 的灵感点赞接口
 * - isInspiration 为 false 时，使用活动专用点赞接口
 */
export const useActivityInteraction = ({
  onInteractionStatusChange,
  interactionPosition,
}: UseActivityInteractionConfig = {}) => {
  const trans = useTranslations();
  const pathname = usePathname();

  const { vote: inspirationVote, unVote: inspirationUnVote, loading: inspirationLoading } = useCommunityInteraction({});

  const { run: activityLike, loading: activityLiking } = useFetch(
    ({ activityId, id }: { activityId: string; id: string }) =>
      activitiesLikeWork({
        path: { work_id: id },
        body: { activity_id: activityId },
      }),
    { manual: true },
  );

  const { run: activityUnlike, loading: activityUnliking } = useFetch(
    ({ activityId, id }: { activityId: string; id: string }) =>
      activitiesCancelLikeWork({
        path: { work_id: id },
        query: { activity_id: activityId },
      }),
    { manual: true },
  );

  // 点赞
  const vote = useCallback(
    async (params: ActivityInteractionParams) => {
      const { activityId, id, obj_type, isInspiration, traceId } = params;

      onInteractionStatusChange?.({ id, voted: true });

      // 根据 isInspiration 判断使用哪个接口
      if (isInspiration) {
        inspirationVote({ id, obj_type, traceId });
      } else {
        // 活动点赞需要单独处理埋点
        reportEvent(TRACE_EVENT_NAME[obj_type], {
          [TRACE_EVENT_ID[obj_type]]: id,
          interaction_type: 'like',
          interaction_position: interactionPosition || pathname,
          trace_id: traceId || '',
          content_type: obj_type === CommunityEnum.ImageAsset ? 'image' : 'video',
        });

        try {
          await activityLike({ activityId, id });
        } catch {
          onInteractionStatusChange?.({ id, voted: false });
          toast({
            type: 'error',
            message: trans('popup.toast.operationFailed'),
          });
        }
      }
    },
    [activityLike, inspirationVote, onInteractionStatusChange, interactionPosition, trans],
  );

  // 取消点赞
  const unVote = useCallback(
    async (params: ActivityInteractionParams) => {
      const { activityId, id, obj_type, isInspiration, traceId } = params;

      onInteractionStatusChange?.({ id, voted: false });

      // 根据 isInspiration 判断使用哪个接口
      if (isInspiration) {
        inspirationUnVote({ id, obj_type, traceId });
      } else {
        // 活动取消点赞需要单独处理埋点
        reportEvent(TRACE_EVENT_NAME[obj_type], {
          [TRACE_EVENT_ID[obj_type]]: id,
          interaction_type: 'cancel_like',
          interaction_position: interactionPosition || pathname,
          trace_id: traceId || '',
          content_type: obj_type === CommunityEnum.ImageAsset ? 'image' : 'video',
        });

        try {
          await activityUnlike({ activityId, id });
        } catch {
          onInteractionStatusChange?.({ id, voted: true });
          toast({
            type: 'error',
            message: trans('popup.toast.operationFailed'),
          });
        }
      }
    },
    [activityUnlike, inspirationUnVote, onInteractionStatusChange, interactionPosition, trans],
  );

  return {
    loading: activityLiking || activityUnliking || inspirationLoading,
    vote,
    unVote,
  };
};
